import React, { useState, useEffect, useCallback } from 'react';
import {
  Upload,
  Layers,
  Database,
  Factory,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  X,
  TrendingUp,
  Package,
  Building2,
  Store,
  Info
} from 'lucide-react';
import { API_URL } from '../../../utils/constant';
import Swal from 'sweetalert2';

const IdealStockImportPage = () => {
  const [selectedBrand, setSelectedBrand] = useState('wsi'); // 'wsi' | 'wsg' | 'reck'
  const [targetField, setTargetField] = useState('ideal_stock'); // 'ideal_stock' | 'wholesale_ideal_stock'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Live Database Stats
  const [dbStats, setDbStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const company_id = localStorage.getItem('company_id') || '4';

  const brandOptions = [
    {
      id: 'wsi',
      name: 'WSI',
      title: 'WSI Minimum Stock',
      badge: 'WSI Minimum Stock.xlsx',
      description: 'Import Dilution, MT, Biochemics, Biocombinations, LM, Specialities & Naturals',
      icon: <Layers size={28} />,
      colorClass: 'bg-primary',
      borderClass: 'border-primary',
      textClass: 'text-primary',
      apiEndpoint: `${API_URL}/ideal-stock/wsi`
    },
    {
      id: 'wsg',
      name: 'WSG',
      title: 'WSG Ideal Stock',
      badge: 'WSG.xlsx',
      description: 'Import WSG Dilutions, Mother Tinctures & Specialities with PR master pricing',
      icon: <Database size={28} />,
      colorClass: 'bg-success',
      borderClass: 'border-success',
      textClass: 'text-success',
      apiEndpoint: `${API_URL}/ideal-stock/wsg`
    },
    {
      id: 'reck',
      name: 'RECKWEG',
      title: 'Reckeweg Minimum Stock',
      badge: 'Minimum Stock RECKWEG.xlsx',
      description: 'Import Reckeweg dilutions, high potency, syrups & specialities with dealer prices',
      icon: <Factory size={28} />,
      colorClass: 'bg-purple',
      borderClass: 'border-purple',
      textClass: 'text-purple',
      apiEndpoint: `${API_URL}/ideal-stock/reck`
    }
  ];

  // Fetch current database stats
  const fetchDbStats = async () => {
    setIsLoadingStats(true);
    try {
      const res = await fetch(`${API_URL}/ideal-stock/stats`);
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          setDbStats(data.data);
        }
      }
    } catch (err) {
      console.warn('Could not fetch ideal stock stats:', err);
    } finally {
      setIsLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchDbStats();
  }, []);

  const handleFileSelect = (file) => {
    if (!file) return;
    const extension = file.name.split('.').pop().toLowerCase();
    if (extension !== 'xlsx' && extension !== 'xls') {
      Swal.fire({
        icon: 'error',
        title: 'Invalid File Format',
        text: 'Please upload an Excel file (.xlsx or .xls)'
      });
      return;
    }
    setUploadedFile(file);
    setError(null);
    setResult(null);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleImport = async () => {
    if (!uploadedFile || !selectedBrand) return;

    const brandConfig = brandOptions.find(b => b.id === selectedBrand);
    const targetLabel = targetField === 'ideal_stock' ? 'Retail Ideal Stock' : 'Wholesale Ideal Stock';

    const confirm = await Swal.fire({
      title: `Import ${brandConfig.name} Stock?`,
      html: `You are about to update <b>${targetLabel}</b> for <b>${brandConfig.name}</b> from file <code>${uploadedFile.name}</code>.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Proceed with Import',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#198754'
    });

    if (!confirm.isConfirmed) return;

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('target_field', targetField);
      formData.append('company_id', company_id);

      const response = await fetch(brandConfig.apiEndpoint, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to import ideal stock file');
      }

      setResult(data);
      Swal.fire({
        icon: 'success',
        title: 'Import Successful',
        text: `Updated ${data.summary?.updated || 0} variations (${data.summary?.created || 0} new entries) for ${brandConfig.name}.`,
        timer: 3500
      });

      // Refresh database stats
      fetchDbStats();
    } catch (err) {
      setError(err.message);
      Swal.fire({
        icon: 'error',
        title: 'Import Failed',
        text: err.message
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setUploadedFile(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="container-fluid py-4 px-md-5">
      {/* Header */}
      <div className="text-center mb-4">
        <h1 className="display-6 fw-bold text-dark mb-1 d-flex justify-content-center align-items-center gap-2">
          <span>Ideal Stock Management</span>
          <span className="badge bg-primary text-white font-monospace fs-6">Master Excel Import</span>
        </h1>
        <p className="text-muted small">
          Upload WSI, WSG, or Reckeweg Excel sheets to update Retail or Wholesale Ideal Stock levels and master dealer pricing in the database.
        </p>
      </div>

      {/* Live DB Statistics Bar */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-0 bg-light p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted text-uppercase fw-bold">Total Variations in DB</small>
                <h4 className="fw-bold mb-0 text-dark">
                  {dbStats ? Number(dbStats.total_records).toLocaleString() : '...'}
                </h4>
              </div>
              <div className="p-3 bg-secondary text-white rounded-circle">
                <Package size={22} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-0 bg-light p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted text-uppercase fw-bold">Retail Ideal Stock Active</small>
                <h4 className="fw-bold mb-0 text-primary">
                  {dbStats ? Number(dbStats.non_zero_retail_stock).toLocaleString() : '...'}
                </h4>
              </div>
              <div className="p-3 bg-primary text-white rounded-circle">
                <Store size={22} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-0 bg-light p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted text-uppercase fw-bold">Wholesale Ideal Stock Active</small>
                <h4 className="fw-bold mb-0 text-success">
                  {dbStats ? Number(dbStats.non_zero_wholesale_stock).toLocaleString() : '...'}
                </h4>
              </div>
              <div className="p-3 bg-success text-white rounded-circle">
                <Building2 size={22} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-0 bg-light p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted text-uppercase fw-bold">Priced Variants</small>
                <h4 className="fw-bold mb-0 text-info">
                  {dbStats ? Number(dbStats.non_zero_price).toLocaleString() : '...'}
                </h4>
              </div>
              <button
                className="btn btn-outline-secondary btn-sm p-2 rounded-circle"
                onClick={fetchDbStats}
                disabled={isLoadingStats}
                title="Refresh Live Statistics"
              >
                <RefreshCw size={18} className={isLoadingStats ? 'fa-spin' : ''} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Import Card */}
      <div className="card shadow-lg border-0 rounded-4">
        <div className="card-header bg-dark text-white py-3 px-4 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold text-white d-flex align-items-center gap-2">
            <FileSpreadsheet size={22} className="text-warning" />
            <span>Select Brand & Stock Target</span>
          </h5>
          {uploadedFile && (
            <button className="btn btn-sm btn-outline-light d-flex align-items-center gap-1" onClick={resetForm}>
              <X size={16} /> Reset
            </button>
          )}
        </div>

        <div className="card-body p-4 p-md-5">
          {/* 1. Brand Selection Cards */}
          <div className="mb-4">
            <label className="form-label fw-bold text-muted small text-uppercase mb-3">
              Step 1: Choose Brand
            </label>
            <div className="row g-3">
              {brandOptions.map((brand) => {
                const isSelected = selectedBrand === brand.id;
                return (
                  <div key={brand.id} className="col-12 col-md-4">
                    <div
                      className={`card h-100 p-3 rounded-3 cursor-pointer transition-all ${
                        isSelected
                          ? `border-2 ${brand.borderClass} shadow bg-white`
                          : 'border bg-light text-muted opacity-75'
                      }`}
                      onClick={() => {
                        setSelectedBrand(brand.id);
                        setResult(null);
                        setError(null);
                      }}
                      style={{ cursor: 'pointer', transition: 'all 0.2s ease-in-out' }}
                    >
                      <div className="d-flex align-items-start gap-3">
                        <div className={`p-3 rounded-circle text-white shadow-sm ${brand.colorClass}`}>
                          {brand.icon}
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <h5 className="fw-bold mb-0 text-dark">{brand.name}</h5>
                            {isSelected && (
                              <span className="badge bg-success rounded-pill px-2 py-1">Selected</span>
                            )}
                          </div>
                          <span className="badge bg-light text-secondary border font-monospace mb-2" style={{ fontSize: '11px' }}>
                            {brand.badge}
                          </span>
                          <p className="text-muted small mb-0">{brand.description}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <hr className="my-4" />

          {/* 2. Target Field Selection (Retail vs Wholesale) */}
          <div className="mb-4">
            <label className="form-label fw-bold text-muted small text-uppercase mb-3">
              Step 2: Choose Stock Target Column to Update
            </label>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div
                  className={`p-3 rounded-3 border d-flex align-items-center gap-3 cursor-pointer ${
                    targetField === 'ideal_stock'
                      ? 'border-primary bg-primary bg-opacity-10 shadow-sm'
                      : 'bg-light'
                  }`}
                  onClick={() => setTargetField('ideal_stock')}
                  style={{ cursor: 'pointer' }}
                >
                  <input
                    type="radio"
                    className="form-check-input mt-0 fs-5"
                    name="targetField"
                    checked={targetField === 'ideal_stock'}
                    onChange={() => setTargetField('ideal_stock')}
                  />
                  <div>
                    <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <Store size={18} className="text-primary" />
                      Retail Ideal Stock (<code>ideal_stock</code>)
                    </h6>
                    <small className="text-muted">
                      Updates minimum stock levels for retail shelf & counter dispensing.
                    </small>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div
                  className={`p-3 rounded-3 border d-flex align-items-center gap-3 cursor-pointer ${
                    targetField === 'wholesale_ideal_stock'
                      ? 'border-success bg-success bg-opacity-10 shadow-sm'
                      : 'bg-light'
                  }`}
                  onClick={() => setTargetField('wholesale_ideal_stock')}
                  style={{ cursor: 'pointer' }}
                >
                  <input
                    type="radio"
                    className="form-check-input mt-0 fs-5"
                    name="targetField"
                    checked={targetField === 'wholesale_ideal_stock'}
                    onChange={() => setTargetField('wholesale_ideal_stock')}
                  />
                  <div>
                    <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <Building2 size={18} className="text-success" />
                      Wholesale Ideal Stock (<code>wholesale_ideal_stock</code>)
                    </h6>
                    <small className="text-muted">
                      Updates bulk minimum stock levels for main warehouse distribution.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          {/* 3. Drag & Drop File Upload Zone */}
          <div className="mb-4">
            <label className="form-label fw-bold text-muted small text-uppercase mb-3">
              Step 3: Upload Excel Spreadsheet
            </label>
            <div
              className={`border border-2 border-dashed rounded-4 p-5 text-center transition-all ${
                isDragging ? 'border-primary bg-light' : 'bg-light'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{ position: 'relative' }}
            >
              <input
                type="file"
                className="position-absolute w-100 h-100 opacity-0 top-0 start-0 cursor-pointer"
                accept=".xlsx,.xls"
                onChange={(e) => handleFileSelect(e.target.files[0])}
              />
              <div className="py-3">
                <FileSpreadsheet size={48} className="text-success mb-3" />
                <h5 className="fw-bold text-dark mb-1">
                  {uploadedFile ? uploadedFile.name : `Click or Drag ${brandOptions.find(b => b.id === selectedBrand)?.name} Excel File Here`}
                </h5>
                <p className="text-muted small mb-0">
                  {uploadedFile
                    ? `File size: ${(uploadedFile.size / 1024).toFixed(2)} KB — Ready to import`
                    : 'Accepted formats: Microsoft Excel Spreadsheet (.xlsx, .xls)'}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {uploadedFile && !isProcessing && (
            <div className="d-flex gap-2 mb-4">
              <button
                className="btn btn-primary btn-lg flex-grow-1 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                onClick={handleImport}
              >
                <Upload size={20} />
                <span>Import {brandOptions.find(b => b.id === selectedBrand)?.name} ({targetField === 'ideal_stock' ? 'Retail' : 'Wholesale'})</span>
              </button>
              <button className="btn btn-outline-secondary btn-lg" onClick={resetForm}>
                Cancel
              </button>
            </div>
          )}

          {/* Processing State */}
          {isProcessing && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="fw-bold text-dark">Processing Excel Spreadsheet...</h5>
              <p className="text-muted small">
                Extracting variations, matching product IDs, looking up prices, and updating database table <code>ideal_stock</code>.
              </p>
            </div>
          )}

          {/* Results Summary Box */}
          {result && result.success && (
            <div className="alert alert-success rounded-4 p-4 mt-4 shadow-sm border-0 bg-success bg-opacity-10">
              <div className="d-flex align-items-center gap-2 mb-3">
                <CheckCircle size={26} className="text-success" />
                <h5 className="fw-bold text-success mb-0">Import Completed Successfully</h5>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-12 col-md-3">
                  <div className="bg-white p-3 rounded-3 shadow-sm text-center">
                    <small className="text-muted text-uppercase fw-semibold">Brand</small>
                    <h5 className="fw-bold text-dark mb-0">{result.brand}</h5>
                  </div>
                </div>
                <div className="col-12 col-md-3">
                  <div className="bg-white p-3 rounded-3 shadow-sm text-center">
                    <small className="text-muted text-uppercase fw-semibold">Target Column</small>
                    <h5 className="fw-bold text-primary mb-0 font-monospace">{result.target_field}</h5>
                  </div>
                </div>
                <div className="col-12 col-md-3">
                  <div className="bg-white p-3 rounded-3 shadow-sm text-center">
                    <small className="text-muted text-uppercase fw-semibold">Updated DB Rows</small>
                    <h5 className="fw-bold text-success mb-0">{result.summary?.updated || 0}</h5>
                  </div>
                </div>
                <div className="col-12 col-md-3">
                  <div className="bg-white p-3 rounded-3 shadow-sm text-center">
                    <small className="text-muted text-uppercase fw-semibold">New Created Rows</small>
                    <h5 className="fw-bold text-info mb-0">{result.summary?.created || 0}</h5>
                  </div>
                </div>
              </div>

              {result.unmatched && result.unmatched.length > 0 && (
                <div className="bg-white p-3 rounded-3 border mt-3">
                  <h6 className="fw-bold text-danger mb-2 d-flex align-items-center gap-1">
                    <AlertCircle size={16} /> Unmatched Rows ({result.unmatched.length})
                  </h6>
                  <p className="text-muted small mb-2">
                    These items from the Excel sheet could not be automatically matched to a product in the database:
                  </p>
                  <div className="table-responsive" style={{ maxHeight: '200px' }}>
                    <table className="table table-sm table-bordered mb-0 small">
                      <thead className="table-light">
                        <tr>
                          <th>Code</th>
                          <th>Product Name</th>
                          <th>Category / Sheet</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.unmatched.map((item, idx) => (
                          <tr key={idx}>
                            <td><code>{item.code || '-'}</code></td>
                            <td>{item.name || '-'}</td>
                            <td>{item.category || item.sheet || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IdealStockImportPage;
